import { StyleSheet, Platform } from "react-native";
import { colors, spacing, fontSize, borderRadius, textStyle } from "../../styles";

export const styles = StyleSheet.create({
  /* ─── Root ─── */
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.bg,
    overflow: "hidden",
    position: "relative",
  },

  /* ─── Top bar ─── */
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingTop: 12,
    paddingBottom: 6,
    paddingLeft: spacing.xl,
    paddingRight: 0,        /* notch bleeds to screen edge */
    gap: spacing.md,
    zIndex: 200,
  },
  /* Logotype — left-aligned, takes remaining space */
  logoArea: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  titleText: {
    fontSize: 24,
    ...textStyle.heading,
    color: colors.text,
    letterSpacing: 0.3,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.md,
  },

  /* Style dropdown trigger — dark pill with image preview */
  stylePill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    backgroundColor: colors.primary,
    paddingVertical: 0,
    paddingLeft: 0,
    paddingRight: 16,
    borderRadius: 30,
    height: 80,
    minWidth: 200,
    overflow: "hidden",
  },
  stylePillPreview: {
    width: 80,
    height: 80,
    borderRadius: 30,
    overflow: "hidden",
  },
  stylePillPreviewImg: {
    width: 80,
    height: 80,
  },
  stylePillIcon: {
    fontSize: 18,
  },
  stylePillLabel: {
    fontSize: 14,
    ...textStyle.bodyMedium,
    color: "rgba(255,255,255,0.7)",
  },
  stylePillValue: {
    fontSize: 20,
    ...textStyle.headingMedium,
    color: colors.white,
  },

  /* Icon buttons (chat, kebab) — dark purple circles */
  iconBtn: {
    width: 80,
    height: 80,
    borderRadius: 30,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnNarrow: {
    width: 64,
    height: 80,
    borderRadius: 30,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  iconBtnText: {
    fontSize: 22,
    color: colors.white,
  },

  /* Gallery notch — custom flared shape, right edge */
  galleryBtn: {
    height: 80,
    borderRadius: 32,
    backgroundColor: colors.primary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    alignSelf: "center",
  },

  /* ─── Kebab dropdown ─── */
  kebabDropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    paddingVertical: 4,
    minWidth: 160,
    zIndex: 300,
    ...(Platform.OS === "web"
      ? { boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }
      : { shadowColor: "#000", shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.4, shadowRadius: 16, elevation: 24 }),
  },
  kebabItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  kebabItemText: {
    fontSize: 14,
    ...textStyle.body,
    color: colors.text,
  },

  /* ─── Style dropdown ─── */
  styleDropdown: {
    position: "absolute",
    top: 110,
    right: 0,
    left: 0,
    alignItems: "flex-end",
    paddingRight: 80,
    zIndex: 300,
  },
  styleDropdownInner: {
    backgroundColor: colors.styleDropdownBg,
    borderRadius: 30,
    padding: 24,
    marginTop: 8,
  },
  styleGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  styleOption: {
    alignItems: "center",
    overflow: "hidden",
  },
  stylePreviewClip: {
    overflow: "hidden",
  },
  stylePreviewPlaceholder: {
    backgroundColor: colors.surfaceHover,
  },
  styleOptionText: {
    fontSize: 14,
    color: colors.white,
    ...textStyle.heading,
    textAlign: "center",
  },

  /* ─── Main content: characters | orb | places ─── */
  mainRow: {
    flex: 1,
    flexDirection: "row",
    alignItems: "stretch",
  },

  /* ─── Beat section ─── */
  beatsSection: {
    width: "100%",
    /* height is set inline (flat fraction of window height) — see StoryView. */
  },
  beatsScroll: {
    alignItems: "center",
  },
  beatCard: {
    borderRadius: borderRadius.xl,
    overflow: "hidden" as const,
    borderWidth: 2,
    borderColor: colors.borderBeat,
    ...(Platform.OS === "web"
      ? { boxShadow: "0 5px 10px rgba(0,0,0,0.2), 0 0 30px rgba(0,0,0,0.2)" }
      : { elevation: 8 }),
  },
  beatCardFocused: {
    borderWidth: 4,
    borderColor: colors.borderFocus,
  },
  beatCardModerated: {
    borderWidth: 3,
    borderColor: "#EF4444",
    opacity: 0.7,
  },
  beatImageWrap: {
    width: "100%",
    height: "100%",
    backgroundColor: colors.surfaceHover,
    alignItems: "center",
    justifyContent: "center",
  },
  beatImage: {
    width: "100%",
    height: "100%",
  },
  beatTextArea: {
    marginTop: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  beatText: {
    fontSize: 16,
    ...textStyle.body,
    color: colors.text,
    lineHeight: 24,
  },
  beatLoading: {
    fontSize: fontSize.xl,
  },

  generateBtn: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    minWidth: 176,
    backgroundColor: colors.primary,
    borderRadius: 32,
    alignItems: "center",
  },
  generateBtnDisabled: {
    opacity: 0.35,
  },
  generateBtnText: {
    fontSize: 16,
    ...textStyle.heading,
    color: colors.white,
  },

  /* ─── Shared backdrop ─── */
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 199,
  },
  arcMenuItem: {
    position: "absolute",
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
  },
  arcMenuEmoji: {
    fontSize: 22,
  },
  floatingPanel: {
    position: "absolute",
    bottom: 110,
    left: spacing.lg,
    backgroundColor: colors.surface,
    borderRadius: 18,
    padding: spacing.md,
    minWidth: 220,
    zIndex: 200,
  },
  floatingPanelTitle: {
    fontSize: 16,
    ...textStyle.heading,
    color: colors.text,
    paddingBottom: spacing.sm,
    textAlign: "center",
  },
  styleChip: {
    paddingVertical: 10,
    paddingHorizontal: spacing.sm,
    borderRadius: 10,
    marginVertical: 2,
  },
  styleChipActive: {
    backgroundColor: colors.primary,
  },
  styleChipText: {
    fontSize: 15,
    ...textStyle.body,
    color: colors.text,
  },
  styleChipTextActive: {
    color: colors.white,
    ...textStyle.heading,
  },
  savingOverlay: {
    position: "absolute",
    top: 80,
    alignSelf: "center",
    backgroundColor: colors.accent,
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 20,
  },
  savingText: {
    color: colors.white,
    ...textStyle.heading,
    fontSize: 14,
  },
  galleryText: {
    color: colors.white,
    ...textStyle.heading,
  },
});
